import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  dateFormat,
  fileNameFormat,
} from "../../../../services/Utils/functionService";
import {
  deleteFile,
  downloadDoc,
  openDoc,
} from "../../../../services/Api/documentService";
import { getDocRequest } from "../../../../services/Api/requestService";
import Eye from "../../../../assets/icons/eye.svg";
import Trash from "../../../../assets/icons/trash.svg";
import Download from "../../../../assets/icons/download.svg";
import Right from "../../../../assets/icons/chevron-right.svg";
import { useCustomerRequestsStore } from "../../../../stores/Cac/Customer/customerRequests";
import RequestDetails from "./RequestDetails";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import { usePartnerRequestsStore2 } from "../../../../stores/Cac/Partner/partnerRequests";
import { showToastError, showToastSuccess } from "../../../../services/Toasts/toasts";
import { DropZoneDocument } from "../../Partner/Utils/DropZoneDocument";
import RequestChat from "./RequestChat";
import { usePartnerRequestDetailsStore } from "../../../../stores/Cac/Partner/partnerRequestDetails";
import { DocumentRequestDetails } from "../../../../interfaces/documentInterface";

const RequestDocuments = () => {
  const { t } = useTranslation();
  // Store
  const {
    requestIdCustomer,
    openDetailsCustomer,
    setOpenDetailsCustomer,
    request,
    setRequest,
    choiceTab,
    setChoiceTab,
  } = useCustomerRequestsStore();

  const { updateRequest, setUpdateRequest } = usePartnerRequestsStore2();
  const { setMessages, setMessage } = usePartnerRequestDetailsStore();

  // Hook
  useClickOutside([
    {
      elementId: "modal-doc-RequestDocuments",
      callback: () => setOpenDetailsCustomer(false),
    },
  ]);

  useEffect(() => {
    if (requestIdCustomer === 0) return;
    getDocRequest(requestIdCustomer)
      .then((data) => {
        if (!data) return;
        setRequest(data[0]);
      })
      .catch(() => showToastError(t("common.error")));
  }, [requestIdCustomer, updateRequest]);

  const handleDeleteFile = async (id: number) => {
    await deleteFile(request.id, id);
    setUpdateRequest(!updateRequest);
    showToastSuccess(t("customerRequests.documentDeleted"));
  };

  useEffect(() => {
    if (!openDetailsCustomer) {
      setMessage("");
      setMessages([]);
    }
  }, [openDetailsCustomer]);

  return (
    <div
      className={`fixed right-0 top-0 z-50 flex h-dvh w-full transform flex-col overflow-hidden bg-dark-lavender2 font-BarlowCondensed shadow-2xl transition-all duration-300 ease-in-out sm:max-w-xl lg:w-5/12 lg:max-w-none xl:w-4/12 2xl:w-3/12 ${
        openDetailsCustomer ? "translate-x-0" : "translate-x-full"
      }`}
      id="modal-doc-RequestDocuments"
    >
      <div className="mx-auto flex w-11/12 flex-shrink-0 flex-col">
        <button
          type="button"
          className="h-10 w-10 flex-shrink-0 cursor-pointer lg:w-20 3xl:h-20"
          onClick={() => {
            setOpenDetailsCustomer(false);
          }}
        >
          <img src={Right} alt="chevron right" />
        </button>
        
        {/* Request ID and status */}
        <div className="mb-3 flex w-full flex-wrap items-center justify-between gap-2 sm:mb-4">
          <p className="min-w-0 truncate font-K2D text-xl text-white sm:text-2xl">
            {t("customerRequests.request")} : {request?.fakeId}{" "}
          </p>
          <div className="flex flex-shrink-0 items-center rounded-lg bg-white/10 px-3 py-1.5 backdrop-blur-sm">
            <p className="mr-2 text-sm font-medium text-white">
              {request?.State?.state}
            </p>
            <div
              className={`h-2.5 w-2.5 rounded-full shadow-inner
                          ${
                            request?.State?.state === "En Attente"
                              ? `bg-yellow-400 shadow-yellow-400/50`
                              : null
                          }
                          ${
                            request?.State?.state === "Délivrée"
                              ? `bg-blue-400 shadow-blue-400/50`
                              : null
                          }
                          ${
                            request?.State?.state === "Accepté"
                              ? `bg-green-400 shadow-green-400/50`
                              : null
                          }
                          ${
                            request?.State?.state === "En retard"
                              ? `bg-orange-400 shadow-orange-400/50`
                              : null
                          }
                          `}
            ></div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mx-auto mb-3 w-11/12 flex-shrink-0 sm:mb-5">
        <div className="w-full overflow-x-auto">
          <div className="flex w-max min-w-full items-center space-x-1 rounded-xl bg-white/10 p-1 backdrop-blur-sm">
            <button 
              type="button" 
              onClick={() => setChoiceTab("Documents")}
              className={`flex-1 cursor-pointer whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 sm:px-4 sm:text-base ${
                choiceTab === "Documents" 
                  ? `bg-white text-gray-800 shadow-lg` 
                  : `text-white hover:bg-white/20`
              }`}
            >
              {t("customerRequests.documents")}
            </button>
            <button 
              type="button" 
              onClick={() => setChoiceTab("Details")}
              className={`flex-1 cursor-pointer whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 sm:px-4 sm:text-base ${
                choiceTab === "Details" 
                  ? `bg-white text-gray-800 shadow-lg` 
                  : `text-white hover:bg-white/20`
              }`}
            >
              {t("customerRequests.details")}
            </button>
            <button
              type="button"
              onClick={() => {
                setChoiceTab("Chat");
                setUpdateRequest(!updateRequest);
              }}
              className={`relative flex-1 cursor-pointer whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 sm:px-4 sm:text-base ${
                choiceTab === "Chat" 
                  ? `bg-white text-gray-800 shadow-lg` 
                  : `text-white hover:bg-white/20`
              }`}
            >
              {request?.chatRead === false && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse shadow-lg"></div>
              )}
              {t("customerRequests.chat")}
            </button>
          </div>
        </div>
      </div>

      {/* Separator */}
      <div className="mx-auto mb-1 w-11/12 flex-shrink-0 sm:mb-5">
        <div className="h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
      </div>

      {/* Content Section */}
      <div className="mx-auto min-h-0 w-11/12 flex-1 overflow-hidden">
        {choiceTab === "Documents" && (
          <div className="h-full flex flex-col w-full">
            {/* Drop Zone */}
            {request?.State?.state !== "Accepté" && (
              <div className="flex-shrink-0">
                <DropZoneDocument
                  requestId={requestIdCustomer}
                  compact
                />
              </div>
            )}

            {/* Document Table */}
            <div className="min-h-0 flex-1 overflow-hidden pb-2 sm:pb-3">
              <div className="relative m-auto flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-lg shadow-slate-900/5">
                <div className="z-10 flex flex-shrink-0 items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-sm sm:px-5 sm:py-4">
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold text-slate-800 sm:text-lg">
                      {t("customerRequests.documentsList")}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
                      Fichiers associés à cette requête
                    </p>
                  </div>
                  <span className="ml-3 inline-flex h-8 min-w-8 flex-shrink-0 items-center justify-center rounded-full bg-light-lavender px-2.5 text-sm font-semibold text-slate-700">
                    {request?.Documents?.length ?? 0}
                  </span>
                </div>

                {!request?.Documents || request?.Documents.length === 0 ? (
                  <div className="flex min-h-52 flex-1 flex-col items-center justify-center px-6 py-10 text-center">
                    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                      <svg
                        className="h-7 w-7"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5A3.375 3.375 0 0010.125 2.25H8.25m0 12.75h7.5m-7.5 3h4.5M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                        />
                      </svg>
                    </div>
                    <p className="font-semibold text-slate-700">
                      {t("customerRequests.noDocuments")}
                    </p>
                    <p className="mt-1 max-w-xs text-sm text-slate-500">
                      Les documents ajoutés à la requête apparaîtront ici.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-1 flex-col gap-2 overflow-y-auto bg-slate-50/70 p-2 sm:p-3">
                    {request?.Documents?.map((document: DocumentRequestDetails) => (
                      <div
                        key={document?.id}
                        className="group relative w-full rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md sm:p-3"
                      >
                        <div className="flex min-w-0 items-center gap-2.5">
                          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-light-lavender text-slate-600">
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5A3.375 3.375 0 0010.125 2.25H8.25m6.75 12H9m3 3H9m1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                              />
                            </svg>
                          </div>

                          <div className="min-w-0 flex-1">
                            <p
                              className="truncate text-sm font-semibold leading-tight text-slate-800 sm:text-base"
                              title={fileNameFormat(document?.filename)}
                            >
                              {fileNameFormat(document?.filename)}
                            </p>

                            <div className="mt-1.5 flex min-w-0 items-center gap-2 overflow-hidden text-xs text-slate-500">
                              <span className="inline-flex min-w-0 items-center gap-1.5">
                                <svg
                                  className="h-3.5 w-3.5 flex-shrink-0"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M6.75 3v2.25M17.25 3v2.25M3.75 18.75V7.5a2.25 2.25 0 012.25-2.25h12A2.25 2.25 0 0120.25 7.5v11.25M3.75 18.75A2.25 2.25 0 006 21h12a2.25 2.25 0 002.25-2.25M3.75 18.75v-7.5h16.5v7.5"
                                  />
                                </svg>
                                <span className="truncate">
                                  {dateFormat(document?.createdAt)}
                                </span>
                              </span>
                              <span className="flex-shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                                {document?.type}
                              </span>
                            </div>
                          </div>

                          <div className="flex flex-shrink-0 items-center gap-1.5">
                            {document.type === "xlsx" || document.type === "zip" ? (
                              <></>
                            ) : (
                              <button
                                type="button"
                                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white transition-all duration-200 hover:border-blue-200 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                onClick={() => {
                                  openDoc(request?.id, document?.id);
                                }}
                                title="Visualiser le document"
                                aria-label="Visualiser le document"
                              >
                                <img
                                  src={Eye}
                                  alt="visualisation"
                                  className="h-4 w-4"
                                />
                              </button>
                            )}
                            <button
                              type="button"
                              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white transition-all duration-200 hover:border-blue-200 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
                              onClick={() => {
                                downloadDoc(
                                  request?.id,
                                  document?.id,
                                  document?.filename
                                );
                              }}
                              title="Télécharger le document"
                              aria-label="Télécharger le document"
                            >
                              <img
                                src={Download}
                                alt="télécharger"
                                className="h-4 w-4"
                              />
                            </button>
                            {choiceTab === "Documents" &&
                            request?.State?.state !== "Accepté" ? (
                              <button
                                type="button"
                                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white transition-all duration-200 hover:border-red-200 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200"
                                onClick={() => handleDeleteFile(document?.id)}
                                title="Supprimer le document"
                                aria-label="Supprimer le document"
                              >
                                <img
                                  className="h-4 w-4"
                                  src={Trash}
                                  alt="supprimer"
                                />
                              </button>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {choiceTab === "Details" && (
          <div className="h-full overflow-hidden">
            <RequestDetails />
          </div>
        )}
        
        {choiceTab === "Chat" && (
          <div className="h-full overflow-hidden">
            <RequestChat />
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestDocuments;
